import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import { StatusResponse } from '../../../../../../../types/API';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { getUseAdvisoryReportQKey } from '../../../../api/getAdvisoryReport';
import {
  AdvisoryReport,
  InsurancePolicy,
} from '../../../../types/AdvisoryReport';

interface Params {
  reportId: string;
  insurancePolicy: InsurancePolicy;
}

export const putInsurancePolicyByReportId = async ({
  reportId,
  insurancePolicy,
}: Params): Promise<StatusResponse> => {
  const { data, isError, error } = await wgApi.put<StatusResponse>(
    `v1/api/advisory-reports/${reportId}/insurance-policies`,
    insurancePolicy,
    { explicitNull: true },
  );
  if (isError) throw error;
  return data;
};

export const usePutInsurancePolicyToReport = (): UseMutationResult<
  StatusResponse,
  unknown,
  Params
> => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ reportId, insurancePolicy }) =>
      putInsurancePolicyByReportId({
        reportId: reportId,
        insurancePolicy: insurancePolicy,
      }),
    {
      onMutate: async ({ insurancePolicy, reportId }) => {
        const qKey = getUseAdvisoryReportQKey(reportId);
        await queryClient.cancelQueries(qKey);

        const previousReport = queryClient.getQueryData<AdvisoryReport>(qKey);

        if (previousReport) {
          // Check if the policy is inside a package
          if (insurancePolicy.policy_package_id) {
            const prevpackages = previousReport.policy_packages;
            const prevPackage = prevpackages?.find(
              ({ id }) => id === insurancePolicy.policy_package_id,
            );
            if (prevPackage) {
              const index = prevPackage.insurance_policies.findIndex(
                ({ id }) => id === insurancePolicy.id,
              );
              const newInsurancePolicies = [...prevPackage.insurance_policies];
              if (index >= 0) {
                newInsurancePolicies[index] = insurancePolicy;
              } else {
                newInsurancePolicies.push(insurancePolicy);
              }
              const newPackage = {
                ...prevPackage,
                insurance_policies: newInsurancePolicies,
              };
              const newPackages =
                prevpackages?.map((pack) => {
                  if (pack.id === insurancePolicy?.policy_package_id) {
                    return newPackage;
                  }
                  return pack;
                }) || [];

              queryClient.setQueryData(qKey, {
                ...previousReport,
                policy_packages: newPackages,
              });
            }
            return { qKey, previousReport };
          }
          // Determine by the type if it needs to be added to the offers array or the current_insurances array
          const policyType =
            insurancePolicy.type === 'ACTIVE' ? 'current_insurances' : 'offers';
          // replace currentinsurance if id already exists or add it to the list if it doesn't
          const newCurrentInsurances = [
            ...previousReport.insurance_policies[policyType],
          ];
          const index = previousReport.insurance_policies[policyType].findIndex(
            ({ id }) => id === insurancePolicy.id,
          );

          if (index >= 0) {
            newCurrentInsurances[index] = insurancePolicy;
          } else {
            newCurrentInsurances.push(insurancePolicy);
          }

          queryClient.setQueryData(qKey, {
            ...previousReport,
            insurance_policies: {
              ...previousReport.insurance_policies,
              // Because uniqBy takes the first uniq value it sees in the array, I reverse it. So it keeps the most recent insurance
              [policyType]: newCurrentInsurances,
            },
          });
        }
        return { qKey, previousReport };
      },
      onError: (_, __, context) => {
        if (context?.previousReport) {
          queryClient.setQueryData(context.qKey, context.previousReport);
        }
      },
    },
  );
};
